// A SZŰRT TÉTELES KÁRTYÁK exportja (a Bevétel-elemzés részletezője fölött). A kártyák adata MÁR a kliensen van,
// pontosan a látható, szűrt halmaz — ezért nincs lapozás: a közös `useListExport` egyetlen „lapot" kap, és így a
// toast-taxonómia és az audit-nyom (ki, mikor, hány sort, milyen szűrővel) ugyanaz, mint a többi listán.
// Egy sor = egy hozzárendelés a kártyán; a szolgáltatás (a kártya címe) oszlopként megy, hogy a táblázat önálló legyen.
import { type TFunction } from 'i18next';
import { useCallback, useMemo, type JSX } from 'react';

import type { DataTableColumn } from '@/components/shared/data-table';
import { ListExportButton } from '@/components/shared/list-export-button';
import { useListExport } from '@/hooks/use-list-export';

interface DetailChild {
  name: string;
  price: string;
}
interface ExportGroup {
  serviceName: string;
  assignments: {
    scope: 'client' | 'account';
    status: string;
    clientName: string;
    accountName: string;
    mrr: string;
    isDue: boolean;
    periodPrice: string;
    nextBilledMonth: string | null;
    frequency: string;
    isOneTime: boolean;
    children: DetailChild[];
  }[];
}
type ExportRow = ExportGroup['assignments'][number] & { serviceName: string };

/** Az export-oszlopok: a kártya minden látható mezője, a felület nyelvén (a fájl a kollégának készül). */
function columnsFor(t: TFunction): DataTableColumn<ExportRow>[] {
  const col = (
    id: string,
    exportValue: (r: ExportRow) => string | number | null,
    exportType?: 'money',
  ): DataTableColumn<ExportRow> => ({
    id,
    header: t(`revenueForecast.details.export.${id}`),
    cell: () => null,
    exportValue,
    ...(exportType ? { exportType } : {}),
  });
  return [
    col('service', (r) => r.serviceName),
    col('client', (r) => r.clientName),
    col('account', (r) => r.accountName || null),
    col('scope', (r) => t(`serviceAssignments.scopeLabel.${r.scope}`, { defaultValue: r.scope })),
    col('frequency', (r) =>
      r.isOneTime
        ? t('revenueForecast.details.oneTime')
        : t(`revenueForecast.frequencyLabel.${r.frequency}`, { defaultValue: r.frequency }),
    ),
    col('status', (r) => t(`revenueForecast.status.${r.status}`, { defaultValue: r.status })),
    col('amount', (r) => r.mrr, 'money'),
    // a TELJES díj (havi tételnél a havidíj, éves tételnél az éves összeg) — a kártya ugyanezt mutatja
    col('periodFee', (r) => r.periodPrice, 'money'),
    col('billedThisMonth', (r) =>
      r.isDue ? t('revenueForecast.details.export.yes') : t('revenueForecast.details.export.no'),
    ),
    col('nextInvoice', (r) => r.nextBilledMonth),
    col('components', (r) => r.children.map((c) => `${c.name}: ${c.price}`).join('; ') || null),
  ];
}

export function ServiceDetailsExport({
  groups,
  month,
  auditFilters,
  t,
}: {
  groups: ExportGroup[];
  month: string;
  /** Az ALKALMAZOTT (a lekérést adó) szűrők pillanatképe az audit-nyomhoz — nem a debounce előtti mező-érték. */
  auditFilters: Record<string, string | undefined>;
  t: TFunction;
}): JSX.Element {
  const rows = useMemo<ExportRow[]>(
    () => groups.flatMap((g) => g.assignments.map((a) => ({ ...a, serviceName: g.serviceName }))),
    [groups],
  );
  const columns = useMemo(() => columnsFor(t), [t]);
  const fetchPage = useCallback(() => Promise.resolve({ rows, next: null }), [rows]);
  const { run, pending } = useListExport({
    columns,
    // a hónap a fájlnévben: a kolléga több hónap exportját egymás mellett tartja
    slug: `revenue-details-${month}`,
    fetchPage,
    filters: auditFilters,
  });
  return <ListExportButton onExport={run} pending={pending} hasRows={rows.length > 0} />;
}
